import { useEffect, useState } from 'react'
import { Dialog } from './Dialog'
import { useHubLang } from '../lib/i18n'
import { COMPOSE } from '../lib/hubCopy'
import {
  DETAIL_KEYS,
  USE_KEYS,
  optedOutOf,
  type DetailKey,
  type PhotoDetails,
  type PhotoUseKey,
  type PhotoUses,
} from '../lib/photos'

/**
 * 148 / 150 (FED-53, FED-54). One photo's opt-out: what it stays out of.
 *
 * David, 10 Oct: no checkbox per photo in the composer. A text link per photo
 * opens this. Posting IS the opt-in (B26, answered); this is finer control,
 * not a barrier, so every box here is an OUT box and nothing is pre-ticked.
 *
 * 150 (David, same day: "we really want to encourage free licence of the
 * photos"): four independent rows —
 *   - Photo contest — opting out costs nothing;
 *   - Marketing, sales, internal training and other uses (THE USE LICENCE) —
 *     the one that earns the 50 stars, and says so when ticked;
 *   - Camera details (camera, date taken) and Location (GPS), separately.
 * For a photo still being posted the two metadata rows stop them being sent;
 * for a photo already on the post they remove what is held, on save — they
 * cannot send them later, the original file is no longer in the browser — and
 * a row with nothing held shows as already out, and fixed.
 *
 * It edits a copy and hands it back on Done; Cancel and Escape change nothing.
 * The reason is asked for only once a USE is ticked out.
 */
export function PhotoOptOut({
  open,
  photoLabel,
  uses,
  reason,
  details,
  detailsAvailable = { camera: true, location: true },
  licenceStars,
  onDone,
  onClose,
}: {
  open: boolean
  /** "Photo 2", or the caption. Shown under the title. */
  photoLabel: string
  uses: PhotoUses
  reason: string
  /** True: sent (a new photo) or held (a kept one). */
  details: PhotoDetails
  /** False for a kept photo with nothing held of that kind. */
  detailsAvailable?: PhotoDetails
  /** What the licence earns (142's rule, 150's trigger). Null when the rule is off. */
  licenceStars: number | null
  onDone: (uses: PhotoUses, reason: string, details: PhotoDetails) => void
  onClose: () => void
}) {
  const lang = useHubLang()
  const t = COMPOSE[lang]
  const [draft, setDraft] = useState<PhotoUses>(uses)
  const [why, setWhy] = useState(reason)
  const [send, setSend] = useState<PhotoDetails>(details)

  // Each opening starts from what is saved, not from a cancelled edit.
  useEffect(() => {
    if (open) {
      setDraft(uses)
      setWhy(reason)
      setSend(details)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const out = optedOutOf(draft)
  const detailOut = (k: DetailKey) => !send[k] || !detailsAvailable[k]
  const all = out.length === USE_KEYS.length && DETAIL_KEYS.every(detailOut)
  const toggle = (k: PhotoUseKey, isOut: boolean) => setDraft((d) => ({ ...d, [k]: !isOut }))

  return (
    <Dialog open={open} title={t.optOutTitle} onClose={onClose} className="optout">
      <p className="optout__photo">{photoLabel}</p>
      <p className="optout__intro">{t.optOutIntro}</p>
      <fieldset className="optout__uses">
        <legend className="sr-only">{t.optOutTitle}</legend>
        {USE_KEYS.map((k) => (
          <label key={k} className="optout__use">
            <input type="checkbox" checked={!draft[k]} onChange={(e) => toggle(k, e.target.checked)} />
            <span>
              <span className="optout__name">{t.useLabel[k]}</span>
              <span className="optout__hint">{k === 'licence' ? t.licenceHint(licenceStars) : t.useHint[k]}</span>
            </span>
          </label>
        ))}
        {DETAIL_KEYS.map((k) => (
          <label key={k} className="optout__use">
            <input
              type="checkbox"
              checked={detailOut(k)}
              disabled={!detailsAvailable[k]}
              onChange={(e) => setSend((s) => ({ ...s, [k]: !e.target.checked }))}
            />
            <span>
              <span className="optout__name">{t.detailLabel[k]}</span>
              <span className="optout__hint">{detailsAvailable[k] ? t.detailHint[k] : t.detailsNotHeld}</span>
            </span>
          </label>
        ))}
        <label className="optout__use optout__use--all">
          <input
            type="checkbox"
            checked={all}
            onChange={(e) => {
              const isOut = e.target.checked
              setDraft({ contest: !isOut, licence: !isOut })
              setSend({ camera: !isOut && detailsAvailable.camera, location: !isOut && detailsAvailable.location })
            }}
          />
          <span className="optout__name">{t.optOutAll}</span>
        </label>
      </fieldset>
      {!draft.licence && licenceStars ? <p className="optout__note">{t.optOutLicenceStars(licenceStars)}</p> : null}
      {out.length > 0 && (
        <label className="optout__reason">
          {t.optOutReason}
          <textarea rows={2} maxLength={280} value={why} onChange={(e) => setWhy(e.target.value)} />
        </label>
      )}
      <div className="optout__actions">
        <button
          type="button"
          className="btn btn--primary"
          onClick={() =>
            onDone(draft, out.length ? why : '', {
              camera: send.camera && detailsAvailable.camera,
              location: send.location && detailsAvailable.location,
            })
          }
        >
          {t.optOutDone}
        </button>
        <button type="button" className="btn btn--quiet" onClick={onClose}>
          {t.cancel}
        </button>
      </div>
    </Dialog>
  )
}

/** The line under a photo once it is opted out of something, e.g. "Out of the photo contest · no location". */
export function optOutSummary(
  t: (typeof COMPOSE)['en'],
  uses: PhotoUses,
  details: PhotoDetails = { camera: true, location: true },
): string | null {
  const parts: string[] = []
  if (!uses.contest && !uses.licence) parts.push(t.optedOutAll)
  else {
    if (!uses.contest) parts.push(t.outOfContest)
    if (!uses.licence) parts.push(t.outOfLicence)
  }
  for (const k of DETAIL_KEYS) if (!details[k]) parts.push(t.noDetail[k])
  return parts.length ? parts.join(' · ') : null
}
